import type { PersonDetailPatch } from '@application/ports';
import type { ParentRole, ShirtSize } from '@domain/vocabulary';

/**
 * Lo que se edita en «Datos de la ficha», tal como lo tiene el formulario.
 *
 * Todo en cadenas porque es lo que devuelven los `<input>` y los `<select>`, y
 * `''` es «vacío». Traducir eso a lo que entiende el servidor es trabajo de
 * `detailPatch`, no de la pantalla.
 */
export interface EditableDetail {
  readonly displayName: string;
  readonly email: string;
  readonly hireDate: string;
  readonly birthDate: string;
  readonly shirtSize: ShirtSize | '';
  readonly parentRole: ParentRole | '';
  /** El id del catálogo de puestos. `''` sólo en las fichas que nunca tuvieron. */
  readonly positionId: string;
}

/**
 * Sólo lo que cambió, y en la forma que el servidor distingue.
 *
 * Una clave ausente es «no lo toques». Por eso no se manda el formulario entero:
 * mandar lo que no cambió reescribiría campos que otra persona pudo corregir
 * mientras éste estaba abierto.
 *
 * Nacimiento, talla y papá o mamá además se pueden **vaciar**, y vacío viaja como
 * `null`, no como `''`: una cadena vacía en `birthDate` no es una fecha, y en
 * `parentRole` `null` es una respuesta —«ninguna de las dos»—. Los tres de arriba
 * no se vacían, así que para ellos basta con mandarlos si cambiaron.
 */
export const detailPatch = (saved: EditableDetail, current: EditableDetail): PersonDetailPatch => {
  const vacioEsNull = <T extends string>(v: T | ''): T | null => (v === '' ? null : v);

  return {
    ...(current.displayName !== saved.displayName ? { displayName: current.displayName } : {}),
    ...(current.email !== saved.email ? { email: current.email } : {}),
    ...(current.hireDate !== saved.hireDate ? { hireDate: current.hireDate } : {}),
    ...(current.birthDate !== saved.birthDate ? { birthDate: vacioEsNull(current.birthDate) } : {}),
    ...(current.shirtSize !== saved.shirtSize ? { shirtSize: vacioEsNull(current.shirtSize) } : {}),
    ...(current.parentRole !== saved.parentRole
      ? { parentRole: vacioEsNull(current.parentRole) }
      : {}),
    /* El puesto no se vacía —el alta lo exige—, así que un `''` no viaja nunca:
       sólo cuenta como cambio elegir otro puesto. */
    ...(current.positionId !== saved.positionId && current.positionId !== ''
      ? { positionId: current.positionId }
      : {}),
  };
};

/** Si hay algo que guardar. */
export const isDirty = (saved: EditableDetail, current: EditableDetail): boolean =>
  Object.keys(detailPatch(saved, current)).length > 0;
